import { useQuery } from "@tanstack/react-query";
import { TaskTable } from "@/components/ValueAssessment/TaskTable";

interface AutomationBenchmark {
  id: string;
  task_label: string;
  pillar: string;
  frequency: string;
  benchmark_percent: number;
  tooltip?: string;
}

export function TaskTableDemo() {
  const { data: tasks, isLoading, error } = useQuery<AutomationBenchmark[]>({
    queryKey: ["/api/automation-benchmarks"],
    queryFn: async () => {
      const response = await fetch("/api/automation-benchmarks");
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }
      const result = await response.json();
      return Array.isArray(result) ? result : (result.benchmarks || []);
    },
  });

  if (isLoading) {
    return <div className="p-6">Loading automation benchmarks...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">Error loading tasks: {(error as Error).message}</div>;
  }

  if (!tasks || !Array.isArray(tasks)) {
    return <div className="p-6 text-red-600">No tasks data received</div>;
  }

  // Group tasks by pillar
  const tasksByPillar = tasks.reduce((acc: Record<string, AutomationBenchmark[]>, task) => {
    if (!acc[task.pillar]) {
      acc[task.pillar] = [];
    }
    acc[task.pillar].push(task);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-2">Automation Benchmark Tasks</h1>
        <p className="text-muted-foreground">
          Displaying {tasks.length} tasks with tooltips from the CSV data
        </p>
      </div>

      {Object.entries(tasksByPillar).map(([pillar, pillarTasks]) => (
        <div key={pillar} className="space-y-4">
          <TaskTable 
            tasks={pillarTasks} 
            title={`${pillar} (${pillarTasks.length} tasks)`}
          />
        </div>
      ))}
    </div>
  );
}
